import React, { useState, useEffect } from 'react'
import { X, MapPin, Package, ArrowRight, Loader2, Move, ScanLine } from 'lucide-react'

interface MoveSlideoutProps {
  isOpen: boolean
  onClose: () => void
  itemData?: {
    id: string
    name: string
    ic: string
    currentLocation: string
    currentLocationId?: string
    currentShelf?: string
    currentTier?: string
    currentTote: string
  }
  onMoveComplete: (data: any) => void
}

export default function MoveSlideout({
  isOpen,
  onClose,
  itemData,
  onMoveComplete
}: MoveSlideoutProps) {
  const [formData, setFormData] = useState({
    newLocation: '',
    newShelf: '',
    newTier: '',
    newTote: '',
    reason: '',
    notes: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [locations, setLocations] = useState<any[]>([])
  const [shelves, setShelves] = useState<any[]>([])
  const [tiers, setTiers] = useState<any[]>([])
  const [totes, setTotes] = useState<any[]>([])
  const [loading, setLoading] = useState(false)

  // Search/filter states for type-ahead
  const [locationSearch, setLocationSearch] = useState('')
  const [shelfSearch, setShelfSearch] = useState('')
  const [tierSearch, setTierSearch] = useState('')
  const [toteSearch, setToteSearch] = useState('')

  // Fetch locations on mount
  useEffect(() => {
    if (isOpen) {
      fetchLocations()
    }
  }, [isOpen])

  // Fetch shelves when location changes
  useEffect(() => {
    if (formData.newLocation) {
      fetchShelves(formData.newLocation)
    } else {
      setShelves([])
      setTiers([])
      setTotes([])
    }
  }, [formData.newLocation])

  // Fetch tiers and totes when shelf changes
  useEffect(() => {
    if (formData.newShelf) {
      fetchTiers(formData.newShelf)
      fetchTotes(formData.newShelf) // Fetch all totes on this shelf
    } else {
      setTiers([])
      setTotes([])
    }
  }, [formData.newShelf])

  const fetchLocations = async () => {
    try {
      setLoading(true)
      const res = await fetch('/api/locations-pg')
      const data = await res.json()
      console.log('Locations API response:', data)
      console.log('Locations data:', data.data)
      setLocations(data.data || [])
      console.log('Locations state set to:', data.data || [])
    } catch (error) {
      console.error('Error fetching locations:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchShelves = async (locationId: string) => {
    try {
      const res = await fetch(`/api/shelves-pg?location_id=${locationId}`)
      const data = await res.json()
      setShelves(data.data || [])
    } catch (error) {
      console.error('Error fetching shelves:', error)
    }
  }

  const fetchTiers = async (shelfId: string) => {
    try {
      const res = await fetch(`/api/tiers-pg?shelf_id=${shelfId}`)
      const data = await res.json()
      setTiers(data.data || [])
    } catch (error) {
      console.error('Error fetching tiers:', error)
    }
  }

  const fetchTotes = async (shelfId: string) => {
    try {
      // Fetch totes by shelf_id (all totes on any tier of this shelf)
      const res = await fetch(`/api/totes-pg?shelf_id=${shelfId}`)
      const data = await res.json()
      setTotes(data.data || [])
    } catch (error) {
      console.error('Error fetching totes:', error)
    }
  }

  // Filtered lists for type-ahead
  const filteredLocations = locations.filter(loc =>
    loc.name?.toLowerCase().includes(locationSearch.toLowerCase())
  )
  const filteredShelves = shelves.filter(shelf =>
    shelf.name?.toLowerCase().includes(shelfSearch.toLowerCase())
  )
  const filteredTiers = tiers.filter(tier =>
    String(tier.position)?.toLowerCase().includes(tierSearch.toLowerCase()) ||
    tier.nickname?.toLowerCase().includes(tierSearch.toLowerCase())
  )
  const filteredTotes = totes.filter(tote =>
    tote.name?.toLowerCase().includes(toteSearch.toLowerCase()) ||
    tote.tote_code?.toLowerCase().includes(toteSearch.toLowerCase())
  )

  // Reset form when slideout opens/closes - default to current location
  useEffect(() => {
    if (isOpen && itemData?.currentLocationId) {
      // Default to current location
      setFormData(prev => ({
        ...prev,
        newLocation: itemData.currentLocationId || ''
      }))
    } else if (!isOpen) {
      setFormData({
        newLocation: '',
        newShelf: '',
        newTier: '',
        newTote: '',
        reason: '',
        notes: ''
      })
      setIsSubmitting(false)
      setLocations([])
      setShelves([])
      setTiers([])
      setTotes([])
      setLocationSearch('')
      setShelfSearch('')
      setTierSearch('')
      setToteSearch('')
    }
  }, [isOpen, itemData?.currentLocationId])

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value }

      // Reset dependent fields when parent changes
      if (field === 'newLocation') {
        updated.newShelf = ''
        updated.newTier = ''
        updated.newTote = ''
      } else if (field === 'newShelf') {
        updated.newTier = ''
        updated.newTote = ''
      } else if (field === 'newTier') {
        updated.newTote = ''
      }

      return updated
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const requestData = {
        location_id: formData.newLocation,
        shelf_id: formData.newShelf || null,
        tier_id: formData.newTier || null,
        tote_id: formData.newTote || null
      }

      console.log('Move request URL:', `/api/items/${itemData?.id}/move-tier`)
      console.log('Move request data:', requestData)
      console.log('Item ID:', itemData?.id)

      // Call the move-tier API
      const response = await fetch(`/api/items/${itemData?.id}/move-tier`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      })

      console.log('Move response status:', response.status, response.statusText)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Move API error response:', errorText)
        let error
        try {
          error = JSON.parse(errorText)
        } catch (e) {
          throw new Error(`Server error: ${response.status} ${response.statusText}`)
        }
        throw new Error(error.error || 'Failed to move item')
      }

      const result = await response.json()

      onMoveComplete({
        itemId: itemData?.id,
        fromLocation: itemData?.currentLocation,
        fromTote: itemData?.currentTote,
        toLocation: formData.newLocation,
        toShelf: formData.newShelf,
        toTier: formData.newTier,
        toTote: formData.newTote,
        reason: formData.reason,
        notes: formData.notes,
        timestamp: new Date().toISOString(),
        result
      })

      onClose()
    } catch (error: any) {
      console.error('Move failed:', error)
      console.error('Error details:', {
        itemId: itemData?.id,
        formData,
        message: error.message,
        stack: error.stack
      })
      alert(error.message || 'Failed to move item. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      overflow: 'hidden'
    }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)'
        }}
      />

      {/* Slide-out panel */}
      <div style={{
        position: 'absolute',
        right: 0,
        top: 0,
        height: '100%',
        width: '480px',
        background: 'linear-gradient(to bottom, #0a0e1a, #1a1f2e)',
        borderLeft: '2px solid rgba(168, 85, 247, 0.5)',
        boxShadow: '-4px 0 40px rgba(168, 85, 247, 0.3), inset 0 0 80px rgba(168, 85, 247, 0.05)',
        transform: 'translateX(0)',
        transition: 'transform 0.3s ease-in-out',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px',
          borderBottom: '1px solid rgba(168, 85, 247, 0.2)',
          background: 'linear-gradient(to right, rgba(168, 85, 247, 0.1), transparent)',
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: 'linear-gradient(to right, transparent, rgba(168, 85, 247, 0.8), transparent)'
          }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              padding: '10px',
              borderRadius: '8px',
              background: 'rgba(168, 85, 247, 0.2)',
              border: '1px solid rgba(168, 85, 247, 0.4)',
              boxShadow: '0 0 20px rgba(168, 85, 247, 0.3)'
            }}>
              <Move style={{ width: '24px', height: '24px', color: '#A855F7' }} />
            </div>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#E0E6F0',
              fontFamily: 'Rajdhani, sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              textShadow: '0 0 20px rgba(168, 85, 247, 0.5)'
            }}>
              Move Item
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '6px',
              padding: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'
              e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.5)'
              e.currentTarget.style.boxShadow = '0 0 15px rgba(239, 68, 68, 0.4)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'
              e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)'
              e.currentTarget.style.boxShadow = 'none'
            }}
          >
            <X style={{ width: '20px', height: '20px', color: '#EF4444' }} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px'
        }}>
          {/* Current Item Section */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.05), rgba(59, 130, 246, 0.05))',
            border: '1px solid rgba(168, 85, 247, 0.3)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '150px',
              height: '150px',
              background: 'radial-gradient(circle, rgba(168, 85, 247, 0.1), transparent)',
              pointerEvents: 'none'
            }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <Package style={{ width: '20px', height: '20px', color: '#A855F7' }} />
              <h3 style={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#C4B5FD',
                fontFamily: 'Rajdhani, sans-serif',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Current Item
              </h3>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#9CA3AF',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '6px'
                }}>
                  Item Name
                </label>
                <div style={{
                  background: 'rgba(17, 24, 39, 0.6)',
                  border: '1px solid rgba(168, 85, 247, 0.2)',
                  borderRadius: '6px',
                  padding: '10px 14px',
                  color: '#E0E6F0',
                  fontSize: '0.875rem'
                }}>
                  {itemData?.name || 'N/A'}
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#9CA3AF',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '6px'
                }}>
                  IC Code
                </label>
                <div style={{
                  background: 'rgba(17, 24, 39, 0.6)',
                  border: '1px solid rgba(168, 85, 247, 0.2)',
                  borderRadius: '6px',
                  padding: '10px 14px',
                  color: '#A855F7',
                  fontSize: '0.875rem',
                  fontFamily: 'monospace',
                  fontWeight: 600
                }}>
                  {itemData?.ic || 'N/A'}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: '#9CA3AF',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '6px'
                  }}>
                    Location
                  </label>
                  <div style={{
                    background: 'rgba(17, 24, 39, 0.6)',
                    border: '1px solid rgba(168, 85, 247, 0.2)',
                    borderRadius: '6px',
                    padding: '10px 14px',
                    color: '#60A5FA',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <MapPin style={{ width: '14px', height: '14px' }} />
                    {itemData?.currentLocation || 'N/A'}
                  </div>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: '#9CA3AF',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '6px'
                  }}>
                    Shelf
                  </label>
                  <div style={{
                    background: 'rgba(17, 24, 39, 0.6)',
                    border: '1px solid rgba(168, 85, 247, 0.2)',
                    borderRadius: '6px',
                    padding: '10px 14px',
                    color: '#A78BFA',
                    fontSize: '0.875rem'
                  }}>
                    {itemData?.currentShelf || 'N/A'}
                  </div>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: '#9CA3AF',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '6px'
                  }}>
                    Tier
                  </label>
                  <div style={{
                    background: 'rgba(17, 24, 39, 0.6)',
                    border: '1px solid rgba(168, 85, 247, 0.2)',
                    borderRadius: '6px',
                    padding: '10px 14px',
                    color: '#F472B6',
                    fontSize: '0.875rem'
                  }}>
                    {itemData?.currentTier || 'N/A'}
                  </div>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: '#9CA3AF',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '6px'
                  }}>
                    Tote
                  </label>
                  <div style={{
                    background: 'rgba(17, 24, 39, 0.6)',
                    border: '1px solid rgba(168, 85, 247, 0.2)',
                    borderRadius: '6px',
                    padding: '10px 14px',
                    color: '#34D399',
                    fontSize: '0.875rem'
                  }}>
                    {itemData?.currentTote || 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* New Destination Section */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.08), rgba(236, 72, 153, 0.08))',
            border: '1px solid rgba(168, 85, 247, 0.3)',
            borderRadius: '12px',
            padding: '20px',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '150px',
              height: '150px',
              background: 'radial-gradient(circle, rgba(236, 72, 153, 0.1), transparent)',
              pointerEvents: 'none'
            }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              <ArrowRight style={{ width: '20px', height: '20px', color: '#EC4899' }} />
              <h3 style={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#F9A8D4',
                fontFamily: 'Rajdhani, sans-serif',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                New Destination
              </h3>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#E0E6F0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '8px'
                }}>
                  New Location * ({filteredLocations.length} of {locations.length})
                </label>
                <input
                  type="text"
                  placeholder="Type to search locations..."
                  value={locationSearch}
                  onChange={(e) => setLocationSearch(e.target.value)}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '8px 14px',
                    background: 'rgba(17, 24, 39, 0.6)',
                    border: '1px solid rgba(168, 85, 247, 0.2)',
                    borderRadius: '6px 6px 0 0',
                    color: '#E0E6F0',
                    fontSize: '0.875rem',
                    outline: 'none',
                    marginBottom: '0'
                  }}
                />
                <select
                  value={formData.newLocation}
                  onChange={(e) => handleInputChange('newLocation', e.target.value)}
                  required
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'rgba(17, 24, 39, 0.8)',
                    border: '2px solid rgba(168, 85, 247, 0.3)',
                    borderRadius: '0 0 6px 6px',
                    borderTop: 'none',
                    color: '#E0E6F0',
                    fontSize: '0.875rem',
                    outline: 'none',
                    transition: 'all 0.2s',
                    cursor: loading ? 'wait' : 'pointer',
                    maxHeight: '200px'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.6)'
                    e.currentTarget.style.boxShadow = '0 0 15px rgba(168, 85, 247, 0.3)'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.3)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                  size={Math.min(filteredLocations.length + 1, 6)}
                >
                  <option value="">Select Location</option>
                  {filteredLocations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#E0E6F0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '8px'
                }}>
                  New Shelf (Optional) ({filteredShelves.length} of {shelves.length})
                </label>
                <input
                  type="text"
                  placeholder="Type to search shelves..."
                  value={shelfSearch}
                  onChange={(e) => setShelfSearch(e.target.value)}
                  disabled={!formData.newLocation || shelves.length === 0}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'rgba(17, 24, 39, 0.8)',
                    border: '2px solid rgba(168, 85, 247, 0.3)',
                    borderRadius: '6px 6px 0 0',
                    color: '#E0E6F0',
                    fontSize: '0.875rem',
                    outline: 'none',
                    transition: 'all 0.2s',
                    cursor: !formData.newLocation ? 'not-allowed' : 'text',
                    opacity: !formData.newLocation ? 0.5 : 1,
                    marginBottom: '0'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.6)'
                    e.currentTarget.style.boxShadow = '0 0 15px rgba(168, 85, 247, 0.3)'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.3)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                />
                <select
                  value={formData.newShelf}
                  onChange={(e) => handleInputChange('newShelf', e.target.value)}
                  disabled={!formData.newLocation || shelves.length === 0}
                  size={Math.min(filteredShelves.length + 1, 6)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'rgba(17, 24, 39, 0.8)',
                    border: '2px solid rgba(168, 85, 247, 0.3)',
                    borderTop: 'none',
                    borderRadius: '0 0 6px 6px',
                    color: '#E0E6F0',
                    fontSize: '0.875rem',
                    outline: 'none',
                    transition: 'all 0.2s',
                    cursor: !formData.newLocation ? 'not-allowed' : 'pointer',
                    opacity: !formData.newLocation ? 0.5 : 1,
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.6)'
                    e.currentTarget.style.boxShadow = '0 0 15px rgba(168, 85, 247, 0.3)'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.3)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                >
                  <option value="">Select Shelf</option>
                  {filteredShelves.map(shelf => (
                    <option key={shelf.id} value={shelf.id}>{shelf.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#E0E6F0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '8px'
                }}>
                  New Tier (Optional) ({filteredTiers.length} of {tiers.length})
                </label>
                <input
                  type="text"
                  placeholder="Type to search tiers..."
                  value={tierSearch}
                  onChange={(e) => setTierSearch(e.target.value)}
                  disabled={!formData.newShelf || tiers.length === 0}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'rgba(17, 24, 39, 0.8)',
                    border: '2px solid rgba(168, 85, 247, 0.3)',
                    borderRadius: '6px 6px 0 0',
                    color: '#E0E6F0',
                    fontSize: '0.875rem',
                    outline: 'none',
                    transition: 'all 0.2s',
                    cursor: !formData.newShelf ? 'not-allowed' : 'text',
                    opacity: !formData.newShelf ? 0.5 : 1,
                    marginBottom: '0'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.6)'
                    e.currentTarget.style.boxShadow = '0 0 15px rgba(168, 85, 247, 0.3)'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.3)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                />
                <select
                  value={formData.newTier}
                  onChange={(e) => handleInputChange('newTier', e.target.value)}
                  disabled={!formData.newShelf || tiers.length === 0}
                  size={Math.min(filteredTiers.length + 1, 6)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'rgba(17, 24, 39, 0.8)',
                    border: '2px solid rgba(168, 85, 247, 0.3)',
                    borderTop: 'none',
                    borderRadius: '0 0 6px 6px',
                    color: '#E0E6F0',
                    fontSize: '0.875rem',
                    outline: 'none',
                    transition: 'all 0.2s',
                    cursor: !formData.newShelf ? 'not-allowed' : 'pointer',
                    opacity: !formData.newShelf ? 0.5 : 1,
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.6)'
                    e.currentTarget.style.boxShadow = '0 0 15px rgba(168, 85, 247, 0.3)'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.3)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                >
                  <option value="">Select Tier</option>
                  {filteredTiers.map(tier => (
                    <option key={tier.id} value={tier.id}>
                      Tier {tier.position}{tier.nickname ? `: ${tier.nickname}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#E0E6F0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '8px'
                }}>
                  New Tote (Optional) ({filteredTotes.length} of {totes.length})
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="Type to search totes..."
                    value={toteSearch}
                    onChange={(e) => setToteSearch(e.target.value)}
                    disabled={!formData.newShelf}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      background: 'rgba(17, 24, 39, 0.8)',
                      border: '2px solid rgba(168, 85, 247, 0.3)',
                      borderRadius: '6px 6px 0 0',
                      color: '#E0E6F0',
                      fontSize: '0.875rem',
                      outline: 'none',
                      transition: 'all 0.2s',
                      cursor: !formData.newShelf ? 'not-allowed' : 'text',
                      opacity: !formData.newShelf ? 0.5 : 1,
                      marginBottom: '0'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.6)'
                      e.currentTarget.style.boxShadow = '0 0 15px rgba(168, 85, 247, 0.3)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.3)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  />
                  <select
                    value={formData.newTote}
                    onChange={(e) => handleInputChange('newTote', e.target.value)}
                    disabled={!formData.newShelf}
                    size={Math.min(filteredTotes.length + 1, 6)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      paddingRight: '48px',
                      background: 'rgba(17, 24, 39, 0.8)',
                      border: '2px solid rgba(168, 85, 247, 0.3)',
                      borderTop: 'none',
                      borderRadius: '0 0 6px 6px',
                      color: '#E0E6F0',
                      fontSize: '0.875rem',
                      outline: 'none',
                      transition: 'all 0.2s',
                      cursor: !formData.newShelf ? 'not-allowed' : 'pointer',
                      opacity: !formData.newShelf ? 0.5 : 1,
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.6)'
                      e.currentTarget.style.boxShadow = '0 0 15px rgba(168, 85, 247, 0.3)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.3)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  >
                    <option value="">No Tote (Loose on Tier)</option>
                    {filteredTotes.map(tote => (
                      <option key={tote.id} value={tote.id}>
                        {tote.name}{tote.tier_position ? ` (Tier ${tote.tier_position})` : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => console.log('Scan tote QR code')}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: 'calc(50% + 20px)',
                      transform: 'translateY(-50%)',
                      background: 'rgba(6, 182, 212, 0.2)',
                      border: '1px solid rgba(6, 182, 212, 0.4)',
                      borderRadius: '4px',
                      padding: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(6, 182, 212, 0.3)'
                      e.currentTarget.style.boxShadow = '0 0 12px rgba(6, 182, 212, 0.4)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(6, 182, 212, 0.2)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  >
                    <ScanLine style={{ width: '16px', height: '16px', color: '#06B6D4' }} />
                  </button>
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#E0E6F0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '8px'
                }}>
                  Reason *
                </label>
                <select
                  value={formData.reason}
                  onChange={(e) => handleInputChange('reason', e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'rgba(17, 24, 39, 0.8)',
                    border: '2px solid rgba(168, 85, 247, 0.3)',
                    borderRadius: '6px',
                    color: '#E0E6F0',
                    fontSize: '0.875rem',
                    outline: 'none',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.6)'
                    e.currentTarget.style.boxShadow = '0 0 15px rgba(168, 85, 247, 0.3)'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.3)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                >
                  <option value="">Select Reason</option>
                  <option value="Reorganization">Reorganization</option>
                  <option value="Picking">Picking for Order</option>
                  <option value="Maintenance">Maintenance</option>
                  <option value="Storage">Storage Optimization</option>
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#E0E6F0',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '8px'
                }}>
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows={3}
                  placeholder="Additional notes about the move..."
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    background: 'rgba(17, 24, 39, 0.8)',
                    border: '2px solid rgba(168, 85, 247, 0.3)',
                    borderRadius: '6px',
                    color: '#E0E6F0',
                    fontSize: '0.875rem',
                    outline: 'none',
                    transition: 'all 0.2s',
                    resize: 'vertical',
                    fontFamily: 'system-ui, sans-serif'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.6)'
                    e.currentTarget.style.boxShadow = '0 0 15px rgba(168, 85, 247, 0.3)'
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.3)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                />
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                paddingTop: '8px'
              }}>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    background: 'rgba(107, 114, 128, 0.2)',
                    border: '2px solid rgba(107, 114, 128, 0.4)',
                    borderRadius: '8px',
                    color: '#9CA3AF',
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    fontFamily: 'Rajdhani, sans-serif'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(107, 114, 128, 0.3)'
                    e.currentTarget.style.borderColor = 'rgba(107, 114, 128, 0.6)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(107, 114, 128, 0.2)'
                    e.currentTarget.style.borderColor = 'rgba(107, 114, 128, 0.4)'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    background: isSubmitting
                      ? 'rgba(168, 85, 247, 0.3)'
                      : 'linear-gradient(135deg, #A855F7, #EC4899)',
                    border: '2px solid rgba(168, 85, 247, 0.6)',
                    borderRadius: '8px',
                    color: '#FFFFFF',
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontFamily: 'Rajdhani, sans-serif',
                    boxShadow: isSubmitting ? 'none' : '0 0 20px rgba(168, 85, 247, 0.4)',
                    opacity: isSubmitting ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isSubmitting) {
                      e.currentTarget.style.boxShadow = '0 0 30px rgba(168, 85, 247, 0.6)'
                      e.currentTarget.style.transform = 'scale(1.02)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSubmitting) {
                      e.currentTarget.style.boxShadow = '0 0 20px rgba(168, 85, 247, 0.4)'
                      e.currentTarget.style.transform = 'scale(1)'
                    }
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
                      Moving...
                    </>
                  ) : (
                    <>
                      <ArrowRight style={{ width: '16px', height: '16px' }} />
                      Move Item
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
